/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/CardTabOrderedBlocks.tsx
 * FILE_ID: UI-KANBAN-CARD-TAB-LAYOUT-BLOCKS-001
 * PURPOSE: Render Card-tab blocks in the operator's saved order.
 *   Hidden ids are omitted here; their data stays on the card.
 * WHY: Spine plus leftover catalog share one visible list.
 * NOTES FOR FUTURE AI: Leftover nodes come from renderCardTabLeftovers.
 *   Do not hardcode the stack again.
 */
import type { ReactNode } from 'react'
import type { KanbanApi, KanbanEmailAdapter, KanbanMember } from '../../../api'
import type { Card } from '../../../types'
import { CardTeamsThreadSection } from '../../CardTeamsThreadSection'
import { CommentsSection } from '../CommentsSection'
import { CardLinkedEmailSection } from '../CardLinkedEmailSection'
import type { DrawerData, DrawerEditing } from '../drawerHookTypes'
import { CardDescriptionSection } from './CardDescriptionSection'
import { ActionSpineSection } from './ActionSpineSection'
import type { CardTabBlockId } from './cardTabLayout'

export interface CardTabOrderedBlocksProps {
  visible: CardTabBlockId[]
  leftovers?: Partial<Record<CardTabBlockId, ReactNode>>
  card: Card
  collabCard: Card
  api: KanbanApi
  boardId?: string
  members?: KanbanMember[]
  currentUserId?: number | null
  emailAdapter?: KanbanEmailAdapter
  editing: DrawerEditing
  data: DrawerData
  openEmailTab: (emailId?: string) => void
}

export function CardTabOrderedBlocks({
  visible,
  leftovers,
  card,
  collabCard,
  api,
  boardId,
  members,
  currentUserId,
  emailAdapter,
  editing,
  data,
  openEmailTab,
}: CardTabOrderedBlocksProps) {
  const { qc, pushFocusedCardUpdate } = data
  return (
    <>
      {visible.map((id) => {
        if (leftovers && id in leftovers) {
          const node = leftovers[id]
          return node ? <div key={id}>{node}</div> : null
        }
        switch (id) {
          case 'action':
            return <ActionSpineSection key={id} card={card} editing={editing} />
          case 'teamChat':
            return api.fetchCardTeamsThread ? (
              <CardTeamsThreadSection
                key={id}
                card={collabCard}
                api={api}
                members={members}
                currentUserId={currentUserId}
              />
            ) : null
          case 'cardNotes':
            return (
              <CommentsSection
                key={id}
                card={collabCard}
                api={api}
                qc={qc}
                boardId={boardId}
                members={members}
                currentUserId={currentUserId}
              />
            )
          case 'email':
            // EMAIL SLOT (ARC-1 owns contents; ARC-4 owns placement).
            return emailAdapter ? (
              <CardLinkedEmailSection
                key={id}
                card={card}
                api={api}
                emailAdapter={emailAdapter}
                boardId={boardId}
                qc={qc}
                onChanged={pushFocusedCardUpdate}
                onOpenEmail={openEmailTab}
              />
            ) : null
          case 'description':
            return (
              <CardDescriptionSection
                key={id}
                card={card}
                api={api}
                members={members}
                boardId={boardId}
                editing={editing}
                data={data}
              />
            )
          default:
            return null
        }
      })}
    </>
  )
}
